import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Button — Koom GO UI Kit primitive (MA-03)
 *
 * Design system compliance:
 *   - Font:    font-sans → Plus Jakarta Sans (AP-01: no Inter)
 *   - Primary: tg-button-bg / tg-button-text — Telegram themeParams-aware (light & dark)
 *   - Colors:  brand-primary (warm amber, tokens.color.brand.primary.500), neutral scale (AP-02: no purple/violet)
 *   - Shadows: tokens.shadow.{sm,md,lg} only (AP-06: no colored neon glow)
 *   - Motion:  duration-fast (180ms hover) + duration-instant (100ms active) + ease-standard
 *              scale(0.97) on active per motion.md §3.1 (AP-05: no spring/bounce)
 *   - Disabled: brand color preserved + opacity-50 (antipattern.md: no full-gray disabled)
 *   - Hover:   max scale-[1.02] to prevent jitter (antipattern.md)
 */
import { forwardRef } from 'react';
import clsx from 'clsx';
// ─── Variant classes ────────────────────────────────────────────────────────────
//
// primary   — Telegram-themeParams-aware CTA. Uses var(--tg-theme-button-color)
//             so the button adapts to both light and dark Telegram themes at
//             runtime. Shadow follows tokens.shadow.md / tokens.shadow.lg.
//             AP-02 rule: no purple gradient. AP-08 rule: no Telegram-blue primary.
//
// secondary — Neutral surface with a subtle border. Text is neutral-900 (dark) so
//             it always satisfies AP-04 (colored-bg + non-gray text contrast rule
//             does not apply — neutral-50 IS a neutral background; text neutral-900
//             provides sufficient contrast).
//
// ghost     — Transparent background. Text uses brand-primary-600 (warm amber,
//             darker than 500 for sufficient contrast on neutral-0 / neutral-50
//             backgrounds per WCAG AA).
//
// danger    — Semantic error surface (DC2626). AP-07: red is exclusively for
//             destructive / error states in Koom GO. Text neutral-0 for contrast.
const VARIANT_CLASSES = {
    primary: [
        'bg-tg-button-bg text-tg-button-text',
        'shadow-md hover:shadow-lg',
        // hover: subtle warm-amber darkening via opacity on the text/icon layer
        'hover:opacity-90',
        // active: translate-y-0.5 + scale feedback per motion.md §3.1
        'active:translate-y-0.5 active:scale-[0.97]',
    ].join(' '),
    secondary: [
        'bg-neutral-50 text-neutral-900',
        'border border-neutral-200',
        'hover:bg-neutral-100',
        'active:bg-neutral-200 active:translate-y-0.5 active:scale-[0.97]',
        'shadow-sm',
    ].join(' '),
    ghost: [
        'bg-transparent text-brand-primary-600',
        'hover:bg-neutral-50',
        'active:bg-neutral-100 active:translate-y-0.5 active:scale-[0.97]',
    ].join(' '),
    danger: [
        'bg-error text-neutral-0',
        'shadow-md hover:shadow-lg',
        'hover:opacity-90',
        'active:translate-y-0.5 active:scale-[0.97]',
    ].join(' '),
};
// ─── Size classes ───────────────────────────────────────────────────────────────
//
// Height anchored in design-system spacing tokens (h-8 = 32px, h-10 = 40px,
// h-12 = 48px). Font scale from tokens.type.scale.
// Radius uses tokens.radius — md (8px) for sm, lg (12px) for md/lg.
const SIZE_CLASSES = {
    sm: 'h-8 px-3 text-body-sm rounded-md gap-1.5',
    md: 'h-10 px-4 text-body rounded-lg gap-2',
    lg: 'h-12 px-6 text-subheading rounded-lg gap-2',
};
// ─── Spinner ────────────────────────────────────────────────────────────────────
//
// Inline border-spinner using currentColor so it inherits the button's text
// color correctly across all variants. animate-spin is a standard Tailwind
// utility (360deg, linear, 1s). Duration is independent of the design-system
// interaction tokens — this is a continuous loader, not an interaction cue.
function Spinner({ size }) {
    const sizeClass = size === 'sm' ? 'w-3.5 h-3.5 border' : 'w-4 h-4 border-2';
    return (_jsx("span", { "aria-hidden": "true", className: clsx(sizeClass, 'inline-block shrink-0', 'border-current border-t-transparent rounded-full animate-spin') }));
}
// ─── Button ─────────────────────────────────────────────────────────────────────
export const Button = forwardRef(({ variant = 'primary', size = 'md', loading = false, fullWidth = false, disabled, className, children, ...rest }, ref) => {
    const isDisabled = disabled || loading;
    return (_jsxs("button", { ref: ref, disabled: isDisabled, "aria-busy": loading || undefined, className: clsx(
        // Layout
        'inline-flex items-center justify-center', 
        // Typography — Plus Jakarta Sans (font-sans), semibold weight
        // AP-01: font-sans maps to Plus Jakarta Sans, never Inter.
        'font-sans font-semibold', 
        // Motion — transition covers: opacity, shadow, transform, background-color.
        // duration-fast (180ms) for hover feedback.
        // ease-standard = cubic-bezier(0.2, 0, 0, 1) — no spring/bounce (AP-05).
        // Active states override duration inline to duration-instant (100ms) via
        // Tailwind's active: pseudo-class on transform for instant tap feel.
        'transition-all duration-fast ease-standard', 
        // Disabled — brand color preserved, just reduced opacity (antipattern.md:
        // disabled state must NOT go full-gray; preserve brand color + opacity-50).
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none', 
        // Variant
        VARIANT_CLASSES[variant], 
        // Size
        SIZE_CLASSES[size], 
        // Full-width
        fullWidth && 'w-full', 
        // Caller overrides
        className), ...rest, children: [loading && _jsx(Spinner, { size: size }), children] }));
});
Button.displayName = 'Button';
//# sourceMappingURL=Button.js.map